import { EventAvailable, PendingActions } from "@mui/icons-material";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  ACCEPT_NON_SYSTEM_APPOINTMENT,
  PENDING_NON_SYSTEM_APPOINTMENT,
} from "../../../../../enums/ApiPaths";
import { APPOINTMENT_GENERAL } from "../../../../../enums/QueryKeys";
import useConfirm from "../../../../../hooks/useConfirm";
import useUser from "../../../../../hooks/useUser";
import ActionRow from "../../../../admin/clinic/components/actions/ActionRow";
import usePutAppointments from "../../../hooks/usePutAppointments";
import {
  canMarkNonSystemAccepted,
  canMarkNonSystemPending,
} from "../../utils/appointmentActionRules";

export function canChangeNonSystemStatus(appointment, clinicType, roles) {
  return (
    canMarkNonSystemAccepted(appointment, clinicType, roles) ||
    canMarkNonSystemPending(appointment, clinicType, roles)
  );
}

/**
 * Lets a master, after contacting a clinic outside Zookeeper, mark its appointment
 * as accepted or move it back to pending. No emails are sent for either.
 */
export default function NonSystemStatusActions({ appointment, clinicType }) {
  const { t } = useTranslation();
  const { roles } = useUser();
  const { id } = useParams();
  const accept = usePutAppointments(
    ACCEPT_NON_SYSTEM_APPOINTMENT + id,
    APPOINTMENT_GENERAL + id,
  );
  const pending = usePutAppointments(
    PENDING_NON_SYSTEM_APPOINTMENT + id,
    APPOINTMENT_GENERAL + id,
  );
  const { ConfirmationDialog, confirm, getMessage } = useConfirm(
    t("AreYouSure"),
    t("Accept"),
    t("Cancel"),
    "warning",
  );

  const canAccept = canMarkNonSystemAccepted(appointment, clinicType, roles);
  const canPending = canMarkNonSystemPending(appointment, clinicType, roles);

  if (!canAccept && !canPending) {
    return null;
  }

  async function handleChange(confirmMessage, put) {
    getMessage(t(confirmMessage));
    if (await confirm()) {
      put.submitCallback();
    }
  }

  return (
    <Fragment>
      {canAccept && (
        <ActionRow
          justButton
          icon={<EventAvailable />}
          label={t("MarkAppointmentAccepted")}
          description={t("MarkAppointmentAcceptedDesc")}
          loading={accept.loading}
          onClick={() => handleChange("MarkAppointmentAcceptedConfirm", accept)}
          testId={"appointment.details.non-system-accept-button"}
        />
      )}
      {canPending && (
        <ActionRow
          justButton
          icon={<PendingActions />}
          label={t("MarkAppointmentPending")}
          description={t("MarkAppointmentPendingDesc")}
          loading={pending.loading}
          onClick={() => handleChange("MarkAppointmentPendingConfirm", pending)}
          testId={"appointment.details.non-system-pending-button"}
        />
      )}
      <ConfirmationDialog />
    </Fragment>
  );
}
